/**
 * Поиск и манипуляции с view-элементами (узлы, связи, иерархия, концепты).
 * Все функции принимают modeler первым аргументом.
 */
import {JUNCTION, AND_JUNCTION, OR_JUNCTION} from './constants';
import * as viewHelpers from './viewHelpers';
import * as connectionModel from './ConnectionModel';

function getRefId(ref) {
	if (ref == null) return null;
	if (typeof ref === 'string') return ref;
	return ref.id ?? ref.$attrs?.id ?? null;
}

export function findViewElementById(modeler, id) {
	const view = modeler._currentView;
	if (!view?.viewElements) return null;
	const search = elements => {
		for (const el of elements || []) {
			if ((el.id ?? el.$attrs?.id) === id) return el;
			if (el.viewElements) {
				const found = search(el.viewElements);
				if (found) return found;
			}
		}
		return null;
	};
	return search(view.viewElements);
}

export function moveViewElementToParent(modeler, childId, newParentId) {
	const view = modeler._currentView;
	if (!view?.viewElements) return;
	const getElementId = o => o?.id ?? o?.$attrs?.id;

	const removeFrom = arr => {
		for (let i = 0; i < arr.length; i++) {
			if (getElementId(arr[i]) === childId) {
				const removed = arr.splice(i, 1)[0];
				return removed;
			}
			const ch = arr[i].viewElements || [];
			if (ch.length) {
				const removed = removeFrom(ch);
				if (removed) return removed;
			}
		}
		return null;
	};

	const viewElement = removeFrom(view.viewElements);
	if (!viewElement) return;

	if (newParentId == null) {
		view.viewElements.push(viewElement);
		viewElement.$parent = view;
	} else {
		const parentViewElement = findViewElementById(modeler, newParentId);
		if (!parentViewElement) {
			view.viewElements.push(viewElement);
			viewElement.$parent = view;
			return;
		}
		if (!parentViewElement.viewElements) parentViewElement.viewElements = [];
		parentViewElement.viewElements.push(viewElement);
		viewElement.$parent = parentViewElement;
	}
}

/**
 * Удалить view element по id из дерева view.viewElements (без графа).
 */
export function removeViewElementById(modeler, nodeId) {
	const view = modeler._currentView;
	if (!view?.viewElements) return false;
	const getElementId = o => o?.id ?? o?.$attrs?.id;
	const removeFrom = arr => {
		for (let i = 0; i < arr.length; i++) {
			if (getElementId(arr[i]) === nodeId) {
				arr.splice(i, 1);
				return true;
			}
			const ch = arr[i].viewElements || [];
			if (ch.length && removeFrom(ch)) return true;
		}
		return false;
	};
	return removeFrom(view.viewElements);
}

/**
 * Связь на view (как в renderView.isConnectionViewElement).
 */
function isViewConnectionElement(el) {
	if (!el) return false;
	if (el.waypoints != null) return true;
	return Boolean(
		el.relationshipRef ||
		(el.source && el.target) ||
		(el.$attrs && (el.$attrs.relationshipRef || (el.$attrs.source && el.$attrs.target)))
	);
}

/**
 * Узел на диаграмме: всё, что не связь (включая Note без elementRef, Group, Junction и т.д.).
 */
function isNestedElementViewNode(el) {
	return Boolean(el) && !isViewConnectionElement(el);
}

/**
 * Id всех вложенных узлов-элементов и самого корня, в порядке «сначала потомки, потом родитель»
 * (удобно для удаления с графа без «висящих» детей).
 * @param {Object} viewElement — корневой Node для удаления
 * @returns {string[]}
 */
export function collectDescendantNodeViewIdsPostOrder(viewElement) {
	const ids = [];
	if (!viewElement) return ids;
	const children = viewElement.viewElements || [];
	for (const child of children) {
		if (isNestedElementViewNode(child)) {
			ids.push(...collectDescendantNodeViewIdsPostOrder(child));
		}
	}
	const id = viewElement.id ?? viewElement.$attrs?.id;
	if (id) ids.push(id);
	return ids;
}

export function findViewElementByConceptId(modeler, conceptId, isRelationship) {
	const found = findAllViewElementsByConceptId(modeler, conceptId, isRelationship);
	return found[0] ?? null;
}

export function findAllViewElementsByConceptId(modeler, conceptId, isRelationship) {
	const view = modeler._currentView;
	if (!view?.viewElements || !conceptId) return [];
	const found = [];
	const search = elements => {
		for (const el of elements || []) {
			if (isRelationship) {
				if (getRefId(el.relationshipRef) === conceptId) found.push(el);
			} else if (getRefId(el.elementRef) === conceptId) {
				found.push(el);
			}
			if (el.viewElements) search(el.viewElements);
		}
	};
	search(view.viewElements);
	return found;
}

export function removeConceptFromView(modeler, conceptId, isRelationship) {
	const viewElement = findViewElementByConceptId(modeler, conceptId, isRelationship);
	if (!viewElement) return false;
	const viewElementId = viewElement.id ?? viewElement.$attrs?.id;
	if (!viewElementId) return false;
	const graph = modeler.getGraph();
	if (!graph) return false;
	const cell = graph.getCellById?.(viewElementId);
	if (!cell) return false;
	try {
		graph.removeCell(cell);
		const view = modeler._currentView;
		const viewElements = view?.viewElements;
		if (viewElements && Array.isArray(viewElements)) {
			const removeFrom = arr => {
				for (let i = 0; i < arr.length; i++) {
					const o = arr[i];
					const id = o?.id ?? o?.$attrs?.id;
					if (id === viewElementId) {
						arr.splice(i, 1);
						return true;
					}
					const ch = o.viewElements || [];
					if (ch.length && removeFrom(ch)) return true;
				}
				return false;
			};
			removeFrom(viewElements);
		}
		return true;
	} catch (_) {
		return false;
	}
}

function emitPlacedConnection(modeler, placed) {
	modeler.addConnectionEdge({
		id: placed.connectionId,
		source: placed.sourceId,
		target: placed.targetId,
		vertices: [],
		relationshipType: placed.relationshipType,
		accessType: placed.accessType,
		label: placed.label,
	});
	modeler.getGraph()?.emit?.('connection:added-to-view', {
		connection: {
			identifier: placed.connectionId,
			source: placed.sourceId,
			target: placed.targetId,
			relationshipRef: placed.relationshipRefId,
			type: placed.relationshipType,
			accessType: placed.accessType,
			bendpoints: placed.bendpoints || [],
		},
	});
}

function addRelationshipToView(modeler, conceptId, catalogHint) {
	const model = modeler._model;
	if (!model || !modeler._moddle || !modeler._currentView) return {added: false};
	let relationship = connectionModel.findRelationshipById(model, conceptId);
	if (!relationship && catalogHint) {
		relationship = connectionModel.ensureRelationshipInModel(modeler, {
			identifier: conceptId,
			name: catalogHint.name,
			type: catalogHint.type,
			source: catalogHint.source,
			target: catalogHint.target,
			accessType: catalogHint.accessType,
		});
	}
	if (!relationship) return {added: false};
	const placed = connectionModel.placeExistingRelationship(modeler, relationship);
	if (!placed) return {added: false};
	if (placed.alreadyOnView) {
		selectConcept(modeler, conceptId, true);
		return {added: false, alreadyOnView: true};
	}
	if (placed.missingEndpoints) return {added: false, missingEndpoints: true};
	emitPlacedConnection(modeler, placed);
	requestAnimationFrame(() => {
		selectConcept(modeler, conceptId, true);
	});
	return {added: true, connection: placed};
}

export function addConceptToView(modeler, conceptId, isRelationship, catalogHint) {
	if (isRelationship) return addRelationshipToView(modeler, conceptId, catalogHint);
	const model = modeler._model;
	const moddle = modeler._moddle;
	const view = modeler._currentView;
	if (!model || !moddle || !view) return {added: false};
	const elementsNode = model.elementsNode || model.elements;
	if (!elementsNode) return {added: false};
	if (!Array.isArray(elementsNode.element)) {
		const existing = elementsNode.elements;
		elementsNode.element = Array.isArray(existing) ? existing : [];
	}
	const elements = elementsNode.element;
	let elem = elements.find(e => (e?.id ?? e?.$attrs?.id) === conceptId);
	if (!elem && catalogHint?.type) {
		elem = moddle.create('archimate:BaseElement', {
			id: conceptId,
			type: catalogHint.type,
			name: catalogHint.name ?? catalogHint.type,
		});
		if (moddle.ids?.claim) moddle.ids.claim(conceptId, elem);
		elem.$parent = elementsNode;
		elements.push(elem);
		if (modeler._elementsById && conceptId) modeler._elementsById[conceptId] = elem;
	}
	if (!elem) return {added: false};
	if (findViewElementByConceptId(modeler, conceptId, false)) {
		selectConcept(modeler, conceptId, false);
		return {added: false, alreadyOnView: true};
	}
	const id = moddle.ids?.nextPrefixed?.('id-', 'node') ?? `id-node-${Date.now()}`;
	const x = 150;
	const y = 150;
	const isJunction = elem && [JUNCTION, AND_JUNCTION, OR_JUNCTION].includes(elem.type);
	const w = isJunction ? 20 : 120;
	const h = isJunction ? 20 : 60;
	const viewNode = moddle.create('archimate:Node', {id, elementRef: elem, x, y, w, h});
	if (moddle.ids?.claim) moddle.ids.claim(id, viewNode);
	viewNode.$parent = view;
	if (!view.viewElements) view.viewElements = [];
	view.viewElements.push(viewNode);
	// Добавляем только новый узел на граф, без полного перерендера — иначе с канвы исчезнут остальные элементы
	const type = viewHelpers.getType(modeler, viewNode);
	const label = viewHelpers.getLabel(modeler, viewNode);
	if (viewHelpers.isRenderableElement(modeler, type)) {
		modeler.createElementNode(type, {
			id,
			x,
			y,
			width: w,
			height: h,
			label,
			parent: undefined,
			viewElement: viewNode,
		});
	}
	modeler.getGraph()?.emit?.('node:added-to-view', {
		node: {
			identifier: id,
			elementRef: conceptId,
			x,
			y,
			w,
			h,
			type: elem?.type,
			name: elem?.name ?? elem?.type,
		},
	});
	requestAnimationFrame(() => {
		selectConcept(modeler, conceptId, false);
	});
	return {
		added: true,
		node: {identifier: id, elementRef: conceptId, x, y, w, h},
	};
}

export function selectConcept(modeler, conceptId, isRelationship) {
	const viewElement = findViewElementByConceptId(modeler, conceptId, isRelationship);
	if (!viewElement) return;
	const viewElementId = viewElement.id ?? viewElement.$attrs?.id;
	if (!viewElementId) return;
	focusViewElementByCanvasId(modeler, viewElementId);
}

/**
 * Выделить узел или связь на графе по id view-элемента (canvas identifier) и центрировать вид.
 * @param {*} modeler
 * @param {string} canvasId
 * @returns {boolean}
 */
export function focusViewElementByCanvasId(modeler, canvasId) {
	if (!canvasId || typeof canvasId !== 'string') return false;
	const trimmed = canvasId.trim();
	if (!trimmed) return false;
	const graph = modeler.getGraph();
	if (!graph) return false;
	const cell = graph.getCellById?.(trimmed) ?? graph.model?.getCellById?.(trimmed);
	if (!cell) return false;
	try {
		if (typeof graph.select === 'function') {
			graph.select(cell);
		} else if (typeof graph.selectCellById === 'function') {
			graph.selectCellById(trimmed);
		}
		if (typeof graph.transform?.centerCell === 'function') {
			graph.transform.centerCell(cell);
		}
		return true;
	} catch (_) {
		return false;
	}
}

export function getViewNodeMap(modeler) {
	const view = modeler._currentView;
	const nodeMap = {};
	if (!view?.viewElements) return nodeMap;
	const collect = arr => {
		for (const el of arr || []) {
			if (!el.waypoints && !el.relationshipRef && !(el.source && el.target)) {
				nodeMap[el.id ?? el.$attrs?.id] = el;
				if (el.viewElements) collect(el.viewElements);
			}
		}
	};
	collect(view.viewElements);
	return nodeMap;
}

export function collectElementRefsOnCurrentView(modeler) {
	const refs = [];
	const seen = new Set();
	const view = modeler._currentView;
	const walk = arr => {
		for (const el of arr || []) {
			if (isViewConnectionElement(el)) continue;
			const refId = getRefId(el.elementRef ?? el.$attrs?.elementRef);
			if (refId && !seen.has(refId)) {
				seen.add(refId);
				refs.push(refId);
			}
			if (el.viewElements) walk(el.viewElements);
		}
	};
	walk(view?.viewElements);
	return refs;
}

/**
 * Кладёт на текущий view связь каталога, если оба конца уже на доске.
 * @param {import('../ModelerX6').default} modeler
 * @param {{identifier: string, name?: string, type?: string, source?: string, target?: string, accessType?: string}} dto
 */
export function placeCatalogRelationshipOnView(modeler, dto) {
	if (!dto?.identifier) return {added: false};
	const relationship = connectionModel.ensureRelationshipInModel(modeler, dto);
	if (!relationship) return {added: false};
	const placed = connectionModel.placeExistingRelationship(modeler, relationship);
	if (!placed || placed.alreadyOnView || placed.missingEndpoints) {
		return placed && placed.alreadyOnView
			? {added: false, alreadyOnView: true}
			: {added: false, missingEndpoints: Boolean(placed?.missingEndpoints)};
	}
	emitPlacedConnection(modeler, placed);
	return {added: true, connection: placed};
}
